import { useEffect, useState } from "react";
import { X, MessageCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvent } from "@/lib/analytics";

/**
 * First-session nudge shown ONLY to users whose signup was attributed to the
 * Spot the Pattern quiz (`profile.referral_source === 'quiz'`) and who have
 * not yet seen the nudge (`profile.quiz_nudge_shown === false`).
 *
 * Dismissal or CTA-click flips `quiz_nudge_shown` to true so it never renders again.
 * Non-blocking banner — never a modal.
 */
const QuizNudgeBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from("profiles")
        .select("referral_source, quiz_nudge_shown")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || error || !data) return;
      if (data.referral_source === "quiz" && data.quiz_nudge_shown === false) {
        setVisible(true);
        trackEvent("quiz_nudge_shown", { user_id: user.id });
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const markSeen = async () => {
    if (!user) return;
    await supabase
      .from("profiles")
      .update({ quiz_nudge_shown: true })
      .eq("user_id", user.id);
  };

  const handleAccept = async () => {
    if (dismissing) return;
    setDismissing(true);
    trackEvent("quiz_nudge_accepted", { user_id: user?.id });
    await markSeen();
    setVisible(false);
    navigate("/practice");
  };

  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    trackEvent("quiz_nudge_dismissed", { user_id: user?.id });
    setVisible(false);
    await markSeen();
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Practice conversations nudge"
      className="relative flex items-start gap-4 rounded-xl border border-cyan/30 bg-gradient-to-r from-cyan/10 via-cyan/5 to-transparent px-4 py-4 md:px-5 md:py-4"
    >
      <div className="hidden sm:flex shrink-0 items-center justify-center w-10 h-10 rounded-lg bg-cyan/15 text-cyan">
        <MessageCircle className="w-5 h-5" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm md:text-base font-semibold text-foreground">
          You just practiced spotting it. Want to practice responding to it?
        </p>
        <p className="mt-1 text-xs md:text-sm text-muted-foreground">
          Try a live Practice Conversation — rehearse a calm, steady reply before it counts.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={handleAccept}
            className="inline-flex items-center gap-1.5 rounded-lg bg-cyan px-3.5 py-2 text-xs md:text-sm font-semibold text-primary-foreground hover:brightness-110 transition"
          >
            Start Practice Conversations
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleDismiss}
            className="rounded-lg px-3 py-2 text-xs md:text-sm text-muted-foreground hover:text-foreground transition"
          >
            Not now
          </button>
        </div>
      </div>

      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default QuizNudgeBanner;
